/**
 * The aisle vocabulary, as the only place the user-facing words live.
 *
 * The codes are the wire format and match `Aisles` on the server; the labels are ours
 * alone, so renaming "Fruit & veg" is a change to this file and nothing else. That is the
 * whole reason the server stores codes rather than display names.
 */

/**
 * The eight real aisles, in the order a fairly ordinary supermarket lays them out. This is
 * the fallback when a household has expressed no preference — the server sends its own
 * order with the catalogue, and that is what should be drawn when it is available.
 */
export const AISLES = [
  'produce',
  'bakery',
  'meat_fish',
  'dairy_eggs',
  'frozen',
  'cupboard',
  'drinks',
  'household',
] as const;

/**
 * Where a name nobody has classified ends up. Deliberately not in {@link AISLES}: it
 * always sorts last, it cannot be dragged anywhere, and it is the honest answer rather
 * than a guess. Somebody may still choose it, which says "the table guessed and it guessed
 * wrong" — a different thing from never having been asked.
 */
export const OTHER = 'other';

export type Aisle = (typeof AISLES)[number] | typeof OTHER;

export const AISLE_LABELS: Record<Aisle, string> = {
  produce: 'Fruit & veg',
  bakery: 'Bakery',
  meat_fish: 'Meat & fish',
  dairy_eggs: 'Dairy & eggs',
  frozen: 'Frozen',
  cupboard: 'Cupboard',
  drinks: 'Drinks',
  household: 'Household',
  other: 'Other',
};

const KNOWN = new Set<string>([...AISLES, OTHER]);

/**
 * Narrows a code that came off the wire.
 *
 * Everything the server sends has been through the same closed vocabulary, so this should
 * never refuse anything — but the alternative to checking is a section with no heading and
 * no way to work out why, on a screen somebody is trying to shop from.
 */
export function isAisle(code: string): code is Aisle {
  return KNOWN.has(code);
}

/**
 * The order to draw sections in, given whatever the server said.
 *
 * `other` is appended rather than sorted, because it is not an aisle and a household
 * cannot move it. Anything unrecognised is dropped and anything missing is added back, for
 * the reason the server's own `NormaliseOrder` does the same: a client running an older
 * bundle than the API must not lose a section it has never heard of, and one running a
 * newer bundle must not lose a section the stored order predates.
 */
export function orderAisles(order: readonly string[] | undefined): Aisle[] {
  const chosen = (order ?? []).filter(isAisle).filter((aisle) => aisle !== OTHER);
  const seen = new Set<Aisle>(chosen);

  return [...chosen, ...AISLES.filter((aisle) => !seen.has(aisle)), OTHER];
}
